import React, { useState, useCallback, useEffect, useRef } from 'react';
import './Input.css';
import PlateCalculator from './PlateCalculator';
import { useModal } from '../services/ModalService';
import {
  plateControlRegistry,
  workoutSetEditingRegistry,
  WORKOUT_COACH_CLEARANCE_SETTLE_MS,
} from './floatingControlRegistry';
import { suppressNumericInputContextMenu } from './numericInputContextMenu';

const Input = ({
  value,
  min,
  step,
  type = 'text',
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  editable = true,
  className = '',
  showPlateCalculator = false, // Enable plate calculator for weight fields
  plateStorageKey = null, // Unique key for persisting plate configuration
  clearWorkoutCoachOnFocus = false,
  preserveFocusedDraft = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [focusedDraft, setFocusedDraft] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // Track if modal is open
  const [isCoachClearanceSettling, setIsCoachClearanceSettling] = useState(false);
  const modalService = useModal();
  const inputRef = useRef(null);
  const focusTimeoutRef = useRef(null);
  const floatingControlOwnerRef = useRef(Symbol('workout-input-owner'));

  // Publish editing before React mounts/autofocuses the field so the browser
  // computes its focus scroll with the Coach lane already clear. Plate ownership
  // remains weight-only; every opted-in workout-set field owns Coach clearance.
  useEffect(() => {
    const owner = floatingControlOwnerRef.current;
    const editingActive = isFocused || isModalOpen;
    const coachClearanceActive = editingActive || isCoachClearanceSettling;
    workoutSetEditingRegistry.setActive(owner, clearWorkoutCoachOnFocus && coachClearanceActive);
    plateControlRegistry.setActive(owner, showPlateCalculator && editingActive);
  }, [clearWorkoutCoachOnFocus, showPlateCalculator, isFocused, isModalOpen, isCoachClearanceSettling]);

  useEffect(() => {
    const owner = floatingControlOwnerRef.current;
    return () => {
      workoutSetEditingRegistry.setActive(owner, false);
      plateControlRegistry.setActive(owner, false);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  // Dismiss mobile keyboard when plate calculator modal opens
  useEffect(() => {
    if (isModalOpen && inputRef.current) {
      inputRef.current.blur();
    }
  }, [isModalOpen]);

  const beginEditing = () => {
    if (disabled) return;
    if (preserveFocusedDraft) setFocusedDraft(String(value ?? ''));
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }
    setIsCoachClearanceSettling(false);
    const owner = floatingControlOwnerRef.current;
    if (clearWorkoutCoachOnFocus) {
      workoutSetEditingRegistry.setActive(owner, true);
    }
    if (showPlateCalculator) {
      plateControlRegistry.setActive(owner, true);
    }
    setIsFocused(true);
  };

  const handleFocus = (e) => {
    if (preserveFocusedDraft) setFocusedDraft(e.target.value);
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }
    setIsCoachClearanceSettling(false);
    e.target.select();
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleOpenPlateCalc = useCallback(() => {
    if (showPlateCalculator && !disabled && modalService) {
      // Mark modal as open - this keeps the input rendered while modal is active
      setIsModalOpen(true);
      
      modalService.open(PlateCalculator, {
        initialValue: value,
        onAccept: (newValue) => {
          if (preserveFocusedDraft) setFocusedDraft(String(newValue ?? ''));
          onChange(newValue);
        },
        storageKey: plateStorageKey,
        isUsingUnits: true, // Show unit toggle in modal
        // Callback to handle modal close
        onClose: () => {
          setIsModalOpen(false);
          // We no longer re-focus the input here as it feels "wrong" on mobile 
          // (unexpected keyboard popup). User can tap back into the field if needed.
        }
      });
    }
  }, [showPlateCalculator, disabled, modalService, value, onChange, plateStorageKey, preserveFocusedDraft]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    if (preserveFocusedDraft) setFocusedDraft(newValue);

    if (type === 'number') {
      // Allow intermediate states like "-" or "-."
      if (newValue === '-' || newValue === '-.') {
        onChange(newValue); // Pass the intermediate state to onChange
        return;
      }

      // Remove leading zeroes, but preserve "0" and "0."
      if (/^0+$/.test(newValue)) {
        newValue = '0'; // If all characters are zeroes, reduce to a single "0"
      } else if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
        newValue = newValue.replace(/^0+/, '');
      }

      // If the input is empty, set it to null
      if (newValue === '') {
        onChange(null);
        return;
      }

      // If the input is not a valid number, do nothing
      if (isNaN(newValue)) {
        return;
      }
    }

    onChange(newValue); // Pass the valid value to onChange
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
    if (clearWorkoutCoachOnFocus) {
      setIsCoachClearanceSettling(true);
      focusTimeoutRef.current = setTimeout(() => {
        focusTimeoutRef.current = null;
        setIsCoachClearanceSettling(false);
      }, WORKOUT_COACH_CLEARANCE_SETTLE_MS);
    } else {
      setIsCoachClearanceSettling(false);
    }
    if (onBlur) onBlur(e);

    let newValue = e.target.value;

    if (type === 'number') {
      // If the value is "-" or "-.", reset it to null
      if (newValue === '-' || newValue === '-.') {
        onChange(null);
        return;
      }

      // Convert the value to a number
      newValue = parseFloat(newValue);

      if (isNaN(newValue)) {
        onChange(0); // Default to 0 if the value is invalid
        return;
      }
    }

    onChange(newValue); // Pass the validated value to onChange
  };

  const showInput = isFocused || isModalOpen;
  const showPlateTrigger = isFocused && !isModalOpen;

  const displayValue = showInput
    ? (preserveFocusedDraft && isFocused ? focusedDraft ?? value ?? '' : value ?? '')
    : value !== undefined && value !== null && String(value).trim() !== ''
    ? value
    : type === 'number'
    ? '0' // Ensure the value is always a string
    : ' ';

  const inputElement = editable ? (
    showInput ? (
      <input
        ref={inputRef}
        type={type}
        inputMode={type === 'number' ? 'decimal' : undefined}
        min={min}
        step={step}
        value={displayValue}
        className={className}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onContextMenu={type === 'number' ? suppressNumericInputContextMenu : undefined}
        disabled={disabled}
        readOnly={isModalOpen}
        autoFocus
      />
    ) : (
      <span
        className="underlined-text"
        onClick={beginEditing}
        style={{
          textDecoration: 'underline',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {displayValue}
      </span>
    )
  ) : (
    <span>{displayValue}</span>
  );

  // If plate calculator is enabled, show floating button when focused
  if (showPlateCalculator && editable) {
    return (
      <>
        {inputElement}
        {/* Floating plate calculator trigger - visible only while editing the weight */}
        <div 
          className={`plate-calc-floating-trigger ${showPlateTrigger ? 'visible' : ''}`}
          data-floating-control="plates"
          aria-hidden={!showPlateTrigger}
          inert={!showPlateTrigger ? '' : undefined}
        >
          <button 
            type="button"
            className="plate-calc-floating-btn"
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent blur before click
            }}
            onClick={handleOpenPlateCalc}
            tabIndex={showPlateTrigger ? 0 : -1}
            disabled={disabled}
          >
            Plates
          </button>
        </div>
      </>
    );
  }

  return inputElement;
};

export default Input;